/**
 * The name a "+ Create" button gives a brand new row.
 *
 * Crews, suppliers and products all have a unique name in the database, and
 * every one of those Create buttons wrote the same placeholder every time. The
 * first click worked; the second failed with
 * `duplicate key value violates unique constraint "crews_name_key"`, which
 * tells whoever clicked nothing at all about what to do — and a row still
 * called "New crew", perhaps switched off and a hundred rows down the list, is
 * enough to block creating a crew for ever.
 *
 * So the placeholder counts up. The comparison ignores case and surrounding
 * spaces, which is wider than the constraint: "new crew" and "New crew" do not
 * collide in Postgres, but two rows a click apart that read the same are a
 * worse answer than numbering past them.
 */

/**
 * `base`, or `base 2`, `base 3`… — the first that no existing row is using.
 *
 * @param base     the placeholder, e.g. "New crew".
 * @param existing every name already in the table, active or not.
 */
export function nextRowName(
  base: string,
  existing: Iterable<string | null | undefined>,
): string {
  const taken = new Set<string>();
  for (const name of existing) {
    if (name) taken.add(name.trim().toLowerCase());
  }

  const key = (name: string) => name.trim().toLowerCase();
  if (!taken.has(key(base))) return base;

  // Unbounded in principle; in practice it stops at the first gap, and the
  // list it is walking is the list the user is looking at.
  for (let n = 2; ; n += 1) {
    const candidate = `${base} ${n}`;
    if (!taken.has(key(candidate))) return candidate;
  }
}
